import React, { useState, useEffect } from "react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Switch } from "@/shared/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/shared/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
  User,
  Key,
  Settings,
  Copy,
  Eye,
  EyeOff,
  CheckCircle2,
  Save,
  Info,
  Percent,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import * as engineService from "@/shared/api/services/engineService";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName } from "@/shared/utils";

const providers = [
  { code: "SCB", name: "ธนาคารไทยพาณิชย์", nameEn: "Siam Commercial Bank" },
  { code: "KBANK", name: "ธนาคารกสิกรไทย", nameEn: "Kasikorn Bank" },
  { code: "BBL", name: "ธนาคารกรุงเทพ", nameEn: "Bangkok Bank" },
  { code: "KTB", name: "ธนาคารกรุงไทย", nameEn: "Krungthai Bank" },
  { code: "TRUEWALLET", name: "ทรูมันนี่ วอลเล็ท", nameEn: "TrueMoney Wallet" },
  { code: "PROMPTPAY", name: "พร้อมเพย์ QR", nameEn: "PromptPay QR" },
];

function generateApiKey(prefix = "rsl_pk_") {
  return prefix + Array.from({ length: 32 }, () =>
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}

export default function EngineResellerForm({ open, onClose, reseller, onSave, saving }) {
  const { t, language } = useLanguage();
  const isEdit = !!reseller;

  // Fetch engine profile to show parent commission
  const { data: engineProfile } = useQuery({
    queryKey: ["engine-profile"],
    queryFn: () => engineService.getProfile(),
    enabled: open, // Fetch when form is open (both create and edit)
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    status: "ACTIVE",
    code: "",
    api_key: generateApiKey("rsl_pk_"),
    secret_key: generateApiKey("rsl_sk_"),
    deposit_fee: 0,
    withdrawal_fee: 0,
    percentage_fee: 1.5,
    commission_rate: 0.5,
    max_merchants: 10,
    allowed_providers: [],
    permissions: {
      can_create_merchants: true,
      can_edit_fees: false,
      can_view_webhook_logs: true,
      can_regenerate_api_keys: true,
    },
    contact_name: "",
    contact_phone: "",
    support_email: "",
    notes: "",
  });

  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (reseller) {
      // When editing, populate from reseller data (commission_rate comes from depositCommission)
      setFormData(prev => ({
        ...prev,
        name: reseller.name,
        email: reseller.email,
        password: "",
        status: reseller.status,
        commission_rate: reseller.depositCommission ?? prev.commission_rate,
      }));
    } else {
      // When creating, auto-populate commission_rate from engine profile
      const commissionRate = engineProfile?.depositCommission ?? 0.5;
      setFormData({
        name: "",
        email: "",
        password: "",
        status: "ACTIVE",
        code: "",
        api_key: generateApiKey("rsl_pk_"),
        secret_key: generateApiKey("rsl_sk_"),
        deposit_fee: 0,
        withdrawal_fee: 0,
        percentage_fee: 1.5,
        commission_rate: commissionRate,
        max_merchants: 10,
        allowed_providers: [],
        permissions: {
          can_create_merchants: true,
          can_edit_fees: false,
          can_view_webhook_logs: true,
          can_regenerate_api_keys: true,
        },
        contact_name: "",
        contact_phone: "",
        support_email: "",
        notes: "",
      });
    }
  }, [reseller, open, engineProfile]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleProvider = (code) => {
    const current = formData.allowed_providers || [];
    const updated = current.includes(code)
      ? current.filter((p) => p !== code)
      : [...current, code];
    handleChange("allowed_providers", updated);
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleSubmit = () => {
    if (reseller) {
      // Update - send only updateable fields
      onSave({
        name: formData.name,
        status: formData.status,
      });
    } else {
      // Create - send required fields for backend
      onSave({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        depositCommission: formData.commission_rate || formData.deposit_fee || undefined,
        withdrawalCommission: formData.withdrawal_fee || undefined,
        percentageFee: formData.percentage_fee || undefined,
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEdit ? t("editReseller") : t("createNewReseller")}</SheetTitle>
          <SheetDescription>
            {isEdit ? t("updateResellerDetails") : t("setUpNewResellerPartner")}
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="general" className="mt-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="general" className="text-xs">
              <User className="h-3 w-3 mr-1" />
              {t("generalTab")}
            </TabsTrigger>
            <TabsTrigger value="fees" className="text-xs">
              <Settings className="h-3 w-3 mr-1" />
              {t("feesLimitsTab")}
            </TabsTrigger>
            <TabsTrigger value="api" className="text-xs">
              <Key className="h-3 w-3 mr-1" />
              {t("apiKeys")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("businessNameRequired")}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder={t("businessNamePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("resellerCode")} *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                  placeholder="RSL001"
                  disabled={isEdit}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("loginEmailRequired")}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder={t("loginEmailPlaceholder")}
                disabled={isEdit}
                required
              />
              {isEdit && <p className="text-xs text-slate-500">{t("emailCannotBeChangedAfterCreation")}</p>}
            </div>

            <div className="space-y-2">
              <Label>{isEdit ? t("newPasswordLeaveBlank") : t("passwordRequired")}</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  placeholder={isEdit ? t("enterNewPassword") : t("enterPassword")}
                  required={!isEdit}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {isEdit && (
              <div className="space-y-2">
                <Label>{t("status")}</Label>
                <Select value={formData.status} onValueChange={(v) => handleChange("status", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">{t("active")}</SelectItem>
                    <SelectItem value="INACTIVE">{t("inactive")}</SelectItem>
                    <SelectItem value="SUSPENDED">{t("suspended")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("contactName")}</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => handleChange("contact_name", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("contactPhone")}</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => handleChange("contact_phone", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("supportEmail")}</Label>
              <Input
                type="email"
                value={formData.support_email}
                onChange={(e) => handleChange("support_email", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>{t("allowedProviders")}</Label>
              <div className="grid grid-cols-2 gap-2">
                {providers.map((p) => (
                  <label
                    key={p.code}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer text-sm ${formData.allowed_providers?.includes(p.code)
                      ? "bg-sky-50 border-sky-300"
                      : "bg-white border-slate-200"
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.allowed_providers?.includes(p.code)}
                      onChange={() => toggleProvider(p.code)}
                      className="rounded border-slate-300"
                    />
                    <span>{getProviderName(p, language)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("notes")}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                rows={2}
              />
            </div>
          </TabsContent>

          <TabsContent value="fees" className="space-y-4 mt-4">
            {/* Parent Commission Info (when creating new reseller) */}
            {!isEdit && engineProfile && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex gap-3">
                  <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-900 mb-2">
                      {engineProfile.parentName
                        ? `${t("givenBy")} ${engineProfile.parentType}: ${engineProfile.parentName}`
                        : t("yourCommissionRates")}
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-blue-700 font-medium">{t("depositCommission")}</p>
                        <p className="text-blue-900 text-lg font-semibold">
                          {engineProfile.depositCommission !== null && engineProfile.depositCommission !== undefined
                            ? `${engineProfile.depositCommission}%`
                            : t("nA")}
                        </p>
                      </div>
                      <div>
                        <p className="text-blue-700 font-medium">{t("withdrawalCommission")}</p>
                        <p className="text-blue-900 text-lg font-semibold">
                          {engineProfile.withdrawalCommission !== null && engineProfile.withdrawalCommission !== undefined
                            ? `${engineProfile.withdrawalCommission}%`
                            : t("nA")}
                        </p>
                      </div>
                      <div>
                        <p className="text-blue-700 font-medium">{t("percentageFeePercent")}</p>
                        <p className="text-blue-900 text-lg font-semibold">
                          {engineProfile.percentageFee !== null && engineProfile.percentageFee !== undefined
                            ? `${engineProfile.percentageFee}%`
                            : t("nA")}
                        </p>
                      </div>
                    </div>
                    {engineProfile.parentName && (
                      <p className="text-xs text-blue-600 mt-2">
                        {t("thisCommissionRateSetBy").replace("{name}", engineProfile.parentName)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t("depositFeeThb")}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.deposit_fee}
                  onChange={(e) => handleChange("deposit_fee", parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("withdrawalFeeThb")}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.withdrawal_fee}
                  onChange={(e) => handleChange("withdrawal_fee", parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("percentageFeePercent")}</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.percentage_fee}
                  onChange={(e) => handleChange("percentage_fee", parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("commissionRatePercent")}</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.commission_rate}
                  onChange={(e) => handleChange("commission_rate", parseFloat(e.target.value) || 0)}
                  disabled={true}
                  className="bg-slate-50 cursor-not-allowed"
                />
                <p className="text-xs text-slate-500">
                  {t("thisCommissionRateSetBy").replace("{name}", engineProfile?.parentName || "Admin")}
                </p>
              </div>
              <div className="space-y-2">
                <Label>{t("maxMerchantsLabel")}</Label>
                <Input
                  type="number"
                  value={formData.max_merchants}
                  onChange={(e) => handleChange("max_merchants", parseInt(e.target.value) || 10)}
                />
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t">
              <Label className="text-sm font-medium">{t("permissions")}</Label>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t("canCreateMerchants")}</span>
                  <Switch
                    checked={formData.permissions?.can_create_merchants}
                    onCheckedChange={(v) =>
                      setFormData((prev) => ({
                        ...prev,
                        permissions: { ...prev.permissions, can_create_merchants: v },
                      }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t("canEditFeeStructure")}</span>
                  <Switch
                    checked={formData.permissions?.can_edit_fees}
                    onCheckedChange={(v) =>
                      setFormData((prev) => ({
                        ...prev,
                        permissions: { ...prev.permissions, can_edit_fees: v },
                      }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t("canViewWebhookLogs")}</span>
                  <Switch
                    checked={formData.permissions?.can_view_webhook_logs}
                    onCheckedChange={(v) =>
                      setFormData((prev) => ({
                        ...prev,
                        permissions: { ...prev.permissions, can_view_webhook_logs: v },
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="api" className="space-y-4 mt-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                {t("apiKeysAutoGenerated")}
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t("apiKeyPublic")}</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-2.5 bg-slate-900 rounded-lg font-mono text-xs text-white overflow-x-auto">
                  {showApiKey ? formData.api_key : "••••••••••••••••••••••••••••••••"}
                </div>
                <Button variant="outline" size="icon" onClick={() => setShowApiKey(!showApiKey)}>
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button variant="outline" size="icon" onClick={() => copyToClipboard(formData.api_key, "api")}>
                  {copied === "api" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("secretKey")}</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-2.5 bg-slate-900 rounded-lg font-mono text-xs text-white overflow-x-auto">
                  {showSecretKey ? formData.secret_key : "••••••••••••••••••••••••••••••••"}
                </div>
                <Button variant="outline" size="icon" onClick={() => setShowSecretKey(!showSecretKey)}>
                  {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button variant="outline" size="icon" onClick={() => copyToClipboard(formData.secret_key, "secret")}>
                  {copied === "secret" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={() => onClose(false)}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !formData.name || (!isEdit && (!formData.email || !formData.password))}
            className="gap-2 bg-sky-600 hover:bg-sky-700"
          >
            <Save className="h-4 w-4" />
            {saving ? t("saving") : isEdit ? t("update") : t("create")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}